import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    Box, Typography, Button, Breadcrumbs, Link, Dialog, Grid, Card,
    CardContent, CardActionArea, Chip, Paper, Avatar, Fade, IconButton,
    Tooltip, useTheme
} from '@mui/material';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../services/api';
import update from 'immutability-helper';
import ContentEditor from '../components/course/ContentEditor';
import { Block } from '../components/course/BlockEditor';
import { Parcours, Bloc, Category, Course, Section, VideoContent, QuizContent } from '../types/course';
import { designTokens } from '../theme/designTokens';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ClassIcon from '@mui/icons-material/Class';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Components
import BlocItem from '../components/course/designer/BlocItem';
import BlocModal from '../components/course/modals/BlocModal';
import CategoryModal from '../components/course/modals/CategoryModal';
import CourseModal from '../components/course/modals/CourseModal';
import SectionModal from '../components/course/modals/SectionModal';

import { useToast } from '../context/ToastContext';

const CourseDesigner = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const [parcours, setParcours] = useState<Parcours | null>(null);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();

    // --- Modal States ---
    const [blocModalOpen, setBlocModalOpen] = useState(false);
    const [editingBloc, setEditingBloc] = useState<Bloc | null>(null);

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [selectedBlocId, setSelectedBlocId] = useState<string | null>(null);
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);

    const [courseModalOpen, setCourseModalOpen] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [editingCourse, setEditingCourse] = useState<Course | null>(null);

    const [sectionModalOpen, setSectionModalOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
    const [editingSection, setEditingSection] = useState<Section | null>(null);



    useEffect(() => {
        fetchParcoursDetails();
    }, [id]);

    const fetchParcoursDetails = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            const found = res.data.find((p: Parcours) => p._id === id);
            if (found && found.blocs) {
                found.blocs.sort((a: Bloc, b: Bloc) => (a.order || 0) - (b.order || 0));
            }
            setParcours(found);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching details", err);
            showToast('Erreur lors du chargement du parcours', 'error');
            setLoading(false);
        }
    };

    const moveBloc = useCallback((dragIndex: number, hoverIndex: number) => {
        setParcours((prevParcours: any) =>
            update(prevParcours, {
                blocs: {
                    $splice: [
                        [dragIndex, 1],
                        [hoverIndex, 0, prevParcours.blocs[dragIndex]],
                    ],
                },
            }),
        );
    }, []);

    // --- Handlers ---

    // Bloc
    const handleOpenBlocModal = (bloc: Bloc | null = null) => {
        setEditingBloc(bloc);
        setBlocModalOpen(true);
    };

    const handleSaveBloc = async (data: any) => {
        if (!parcours) return;
        try {
            if (editingBloc) {
                await api.put(`/api/course/bloc/${editingBloc._id}`, data);
            } else {
                await api.post('/api/course/bloc', {
                    ...data,
                    parcoursId: parcours._id
                });
            }
            fetchParcoursDetails();
            showToast('Bloc sauvegardé avec succès', 'success');
        } catch (err) {
            console.error("Error saving bloc", err);
            showToast('Erreur lors de la sauvegarde du bloc', 'error');
        }
    };

    // Category
    const handleOpenCategoryModal = (blocId: string, parentId: string | null = null, category: Category | null = null) => {
        setSelectedBlocId(blocId);
        setSelectedParentId(parentId);
        setEditingCategory(category);
        setCategoryModalOpen(true);
    };

    const handleSaveCategory = async (data: any) => {
        try {
            if (editingCategory) {
                await api.put(`/api/course/category/${editingCategory._id}`, data);
            } else {
                await api.post('/api/course/category', {
                    ...data,
                    blocId: selectedBlocId,
                    parentId: selectedParentId
                });
            }
            fetchParcoursDetails();
            showToast('Catégorie sauvegardée avec succès', 'success');
        } catch (err) {
            console.error("Error saving category", err);
            showToast('Erreur lors de la sauvegarde de la catégorie', 'error');
        }
    };

    // Course
    const handleOpenCourseModal = (categoryId: string | null, course: Course | null = null) => {
        setSelectedCategoryId(categoryId);
        setEditingCourse(course);
        setCourseModalOpen(true);
    };

    const handleSaveCourse = async (data: any) => {
        try {
            if (editingCourse) {
                await api.put(`/api/course/course/${editingCourse._id}`, data);
            } else {
                await api.post('/api/course/course', {
                    ...data,
                    categoryId: selectedCategoryId
                });
            }
            fetchParcoursDetails();
            showToast('Cours sauvegardé avec succès', 'success');
        } catch (err) {
            console.error("Error saving course", err);
            showToast('Erreur lors de la sauvegarde du cours', 'error');
        }
    };

    const handleAiGenerate = async (data: { topic: string; description: string; instructions: string; modelChoose: string }) => {
        try {
            const res = await api.post('/api/course/ai-generate', {
                ...data,
                categoryId: selectedCategoryId
            });
            showToast("Cours généré avec succès", "success");
            // Redirect to course builder
            if (res.data.courseId) {
                navigate(`/course/${res.data.courseId}`);
            }
        } catch (err: any) {
            console.error("Error generating course with AI", err);
            if (err.response && err.response.status === 429) {
                showToast("Quota OpenAI dépassé. Veuillez réessayer plus tard.", "error");
            } else {
                showToast("Erreur lors de la génération du cours", "error");
            }
            throw err;
        }
    };

    // Section
    const handleOpenSectionModal = (courseId: string, section: Section | null = null) => {
        setSelectedCourseId(courseId);
        setEditingSection(section);
        setSectionModalOpen(true);
    };

    const handleSaveSection = async (data: any) => {
        try {
            if (editingSection) {
                await api.put(`/api/course/section/${editingSection._id}`, data);
            } else {
                await api.post('/api/course/section', {
                    ...data,
                    courseId: selectedCourseId
                });
            }
            fetchParcoursDetails();
            showToast('Section sauvegardée avec succès', 'success');
        } catch (err) {
            console.error("Error saving section", err);
            showToast('Erreur lors de la sauvegarde de la section', 'error');
        }
    };



    if (loading) return <Box sx={{ p: 4 }}>Loading...</Box>;
    if (!parcours) return <Box sx={{ p: 4 }}>Parcours not found</Box>;

    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
                p: { xs: 2, md: 4 }
            }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <Link component={RouterLink} underline="hover" color="inherit" to="/parcours" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} /> Gestion des Parcours
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>{parcours.title}</Typography>
                </Breadcrumbs>

                {/* Hero Header */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                        p: { xs: 3, md: 5 },
                        mb: 4,
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decorative circles */}
                    <Box sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 180,
                        height: 180,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        bottom: -30,
                        right: 80,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                    }} />

                    <Grid container spacing={4} alignItems="center">
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Avatar sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    width: 56,
                                    height: 56,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <LayersIcon sx={{ fontSize: 28 }} />
                                </Avatar>
                                <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                    Structure du Parcours
                                </Typography>
                            </Box>
                            <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600, ml: 9 }}>
                                {parcours.title}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenBlocModal()}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#667eea',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.95)',
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Ajouter un Bloc
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={3}>
                    {parcours.blocs.map((bloc, index) => (
                        <Grid size={{ xs: 12, md: 6 }} key={bloc._id}>
                            <Fade in timeout={300 + index * 100}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid rgba(102, 126, 234, 0.15)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        transform: 'translateY(-5px)',
                                        boxShadow: '0 12px 30px rgba(102, 126, 234, 0.15)',
                                    }
                                }}>
                                    <CardActionArea
                                        onClick={() => navigate(`/parcours/${parcours._id}/bloc/${bloc._id}`)}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 40, height: 40 }}>
                                                        <LayersIcon fontSize="small" />
                                                    </Avatar>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif' }}>
                                                        {bloc.title}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title="Éditer le titre/paramètres">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenBlocModal(bloc);
                                                        }}
                                                        sx={{ color: '#667eea' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <Chip
                                                    label={`${bloc.categories?.length || 0} Catégories`}
                                                    size="small"
                                                    sx={{ bgcolor: 'rgba(0,0,0,0.04)', fontWeight: 600 }}
                                                />
                                                {bloc.certificationNumber && (
                                                    <Chip
                                                        label={`Certif: ${bloc.certificationNumber}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600 }}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Fade>
                        </Grid>
                    ))}

                    {parcours.blocs.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Paper sx={{
                                textAlign: 'center',
                                py: 8,
                                borderRadius: 4,
                                background: 'rgba(255,255,255,0.5)',
                                border: '2px dashed #e0e0e0',
                            }}>
                                <LayersIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    Aucun bloc dans ce cursus. Commencez par en ajouter un.
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* Modals */}
                <BlocModal
                    open={blocModalOpen}
                    onClose={() => setBlocModalOpen(false)}
                    onSave={handleSaveBloc}
                    initialData={editingBloc ? { ...editingBloc, order: editingBloc.order || 0 } : undefined}
                    isEditing={!!editingBloc}
                />

                <CategoryModal
                    open={categoryModalOpen}
                    onClose={() => setCategoryModalOpen(false)}
                    onSave={handleSaveCategory}
                    initialData={editingCategory || undefined}
                    isEditing={!!editingCategory}
                    availableCompetencies={selectedBlocId ? parcours.blocs.find(b => b._id === selectedBlocId)?.competencies : []}
                />

                <CourseModal
                    open={courseModalOpen}
                    onClose={() => setCourseModalOpen(false)}
                    onSave={handleSaveCourse}
                    onAiGenerate={handleAiGenerate}
                    initialData={editingCourse || undefined}
                    isEditing={!!editingCourse}
                />

                <SectionModal
                    open={sectionModalOpen}
                    onClose={() => setSectionModalOpen(false)}
                    onSave={handleSaveSection}
                    initialData={editingSection || undefined}
                    isEditing={!!editingSection}
                />

            </Box>
        </DndProvider>
    );
};

export default CourseDesigner;

